import React from 'react';
import { TARGET_TYPE_DECOY, TARGET_TYPE_GOLDEN, TARGET_TYPE_MOVING } from '../../tools/gameEngine';

class GameCanvas extends React.Component {
    constructor(props) {
        super(props);
        this.canvasRef = React.createRef();
        this.animFrameId = null;
        this.lastTime = null;
    }

    componentDidMount() {
        this._setupCanvas();
        this._startLoop();
        window.addEventListener('resize', this._handleResize);
    }

    componentWillUnmount() {
        if (this.animFrameId) cancelAnimationFrame(this.animFrameId);
        window.removeEventListener('resize', this._handleResize);
    }

    _handleResize = () => {
        this._setupCanvas();
    }

    _setupCanvas() {
        const canvas = this.canvasRef.current;
        if (!canvas) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
    }

    _startLoop() {
        const loop = (timestamp) => {
            this.animFrameId = requestAnimationFrame(loop);

            if (!this.lastTime) { this.lastTime = timestamp; return; }
            const dt = Math.min((timestamp - this.lastTime) / 1000, 0.05); // cap at 50ms
            this.lastTime = timestamp;

            const canvas = this.canvasRef.current;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            const rect = canvas.getBoundingClientRect();
            const w = rect.width;
            const h = rect.height;

            const { engine } = this.props;
            if (!engine) return;

            // Update
            engine.update(dt, w, h);

            // Notify parent of state changes
            if (this.props.onUpdate) {
                this.props.onUpdate({
                    score: engine.score,
                    combo: engine.combo,
                    progress: engine.getProgress(),
                    isComplete: engine.gameOver,
                    tutorialHitCount: engine.tutorialHitCount,
                });
            }

            // Render
            this._render(ctx, w, h, engine);
        };
        this.animFrameId = requestAnimationFrame(loop);
    }

    _render(ctx, w, h, engine) {
        // Clear
        ctx.clearRect(0, 0, w, h);

        // Background
        ctx.fillStyle = '#0A0A2E';
        ctx.fillRect(0, 0, w, h);

        // Screen flash
        if (engine.screenFlash > 0) {
            ctx.fillStyle = `rgba(255, 255, 255, ${engine.screenFlash})`;
            ctx.fillRect(0, 0, w, h);
        }

        // Targets
        const baseSize = engine.getBaseTargetSize(w, h);
        for (const t of engine.targets) {
            this._renderTarget(ctx, t, baseSize, engine.elapsedTime);
        }

        // Particles
        for (const p of engine.particles) {
            if (!p.active) continue;
            ctx.globalAlpha = p.alpha;
            ctx.fillStyle = p.color;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.globalAlpha = 1;

        // Popups
        for (const p of engine.popups) {
            ctx.globalAlpha = p.alpha;
            ctx.fillStyle = p.color;
            ctx.font = 'bold 18px -apple-system, BlinkMacSystemFont, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(p.text, p.x, p.y);
        }
        ctx.globalAlpha = 1;
    }

    _renderTarget(ctx, target, baseSize, elapsedTime) {
        const age = elapsedTime - target.spawnTime;
        const scale = target.appearing ? target.scale : 1;
        const r = (baseSize / 2) * scale;

        const isDecoy = target.type === TARGET_TYPE_DECOY;
        const isGolden = target.type === TARGET_TYPE_GOLDEN;
        const isMoving = target.type === TARGET_TYPE_MOVING;

        let baseColor = '#00E5FF'; // standard cyan
        if (isDecoy) baseColor = '#FF3344';
        else if (isGolden) baseColor = '#FFD700';
        else if (isMoving) baseColor = '#8844FF';

        // Moving target trail
        if (isMoving && target.trail && target.trail.length > 1) {
            for (let i = 0; i < target.trail.length - 1; i++) {
                const alpha = (i / target.trail.length) * 0.3;
                const trailR = r * 0.4 * (i / target.trail.length);
                ctx.globalAlpha = alpha;
                ctx.fillStyle = baseColor;
                ctx.beginPath();
                ctx.arc(target.trail[i].x, target.trail[i].y, trailR, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.globalAlpha = 1;
        }

        // Approach ring (shrinking timer)
        if (!target.appearing) {
            const progress = age / target.duration;
            const ringR = r + 12 * (1 - progress);
            const ringAlpha = 0.5 * (1 - progress);
            ctx.strokeStyle = `rgba(255, 255, 255, ${ringAlpha})`;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(target.x, target.y, ringR, 0, Math.PI * 2);
            ctx.stroke();
        }

        // Glow
        ctx.shadowColor = baseColor;
        ctx.shadowBlur = isGolden ? 25 * scale : 15 * scale;

        // Main circle
        ctx.fillStyle = baseColor;
        ctx.beginPath();
        ctx.arc(target.x, target.y, r, 0, Math.PI * 2);
        ctx.fill();

        // Inner highlight
        ctx.shadowBlur = 0;
        ctx.fillStyle = isDecoy ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.2)';
        ctx.beginPath();
        ctx.arc(target.x, target.y, r * 0.5, 0, Math.PI * 2);
        ctx.fill();

        // Decoy X mark
        if (isDecoy) {
            ctx.strokeStyle = 'rgba(255,255,255,0.5)';
            ctx.lineWidth = 2.5;
            const xSize = r * 0.4;
            ctx.beginPath();
            ctx.moveTo(target.x - xSize, target.y - xSize);
            ctx.lineTo(target.x + xSize, target.y + xSize);
            ctx.moveTo(target.x + xSize, target.y - xSize);
            ctx.lineTo(target.x - xSize, target.y + xSize);
            ctx.stroke();
        }

        // Golden sparkle effect — rotating star points
        if (isGolden && !target.appearing) {
            const sparkleAngle = elapsedTime * 3;
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
            ctx.lineWidth = 1.5;
            for (let i = 0; i < 4; i++) {
                const a = sparkleAngle + (Math.PI / 2) * i;
                const innerR = r * 0.3;
                const outerR = r * 0.7;
                ctx.beginPath();
                ctx.moveTo(target.x + Math.cos(a) * innerR, target.y + Math.sin(a) * innerR);
                ctx.lineTo(target.x + Math.cos(a) * outerR, target.y + Math.sin(a) * outerR);
                ctx.stroke();
            }
        }

        // Moving target direction indicator — small arrow
        if (isMoving && !target.appearing) {
            const angle = Math.atan2(target.vy, target.vx);
            const arrowLen = r * 0.6;
            ctx.strokeStyle = 'rgba(255,255,255,0.3)';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(target.x, target.y);
            ctx.lineTo(target.x + Math.cos(angle) * arrowLen, target.y + Math.sin(angle) * arrowLen);
            ctx.stroke();
        }

        // Pulse animation for idle targets
        if (!target.appearing) {
            const pulseSpeed = isGolden ? 4 : 2;
            const pulse = 1 + Math.sin(elapsedTime * Math.PI * pulseSpeed) * 0.03;
            ctx.shadowColor = baseColor;
            ctx.shadowBlur = 15 * pulse;
        }

        // Reset shadow
        ctx.shadowBlur = 0;
        ctx.shadowColor = 'transparent';
    }

    _handlePointerDown = (e) => {
        e.preventDefault();
        if (!this.props.engine || this.props.engine.paused || this.props.engine.gameOver) return;

        const canvas = this.canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const result = this.props.engine.handleTap(x, y, rect.width, rect.height);
        if (result && this.props.onTap) {
            this.props.onTap(result);
        }
    }

    render() {
        return (
            <canvas
                ref={this.canvasRef}
                className="game-canvas"
                data-testid="game-canvas"
                onPointerDown={this._handlePointerDown}
                style={{
                    position: 'absolute',
                    top: 0, left: 0, width: '100%', height: '100%',
                    touchAction: 'manipulation',
                    userSelect: 'none',
                    WebkitTouchCallout: 'none',
                }}
            />
        );
    }
}

export default GameCanvas;
